import { useEffect, useRef, useState } from "react";
import { playTypeTick } from "@/components/shared/ambient-sound";

// Scrambles characters and resolves to final text, char-by-char with stagger.
// Lightweight, no deps. Used in the entry experience headline.
const GLYPHS = "!<>-_\\/[]{}—=+*^?#________░▒▓█AKIRA0X1Z";

export default function TextChaos({
  as: Tag = "h2",
  text = "",
  className = "",
  stagger = 140,
  cycles = 22,
  frameStep = 3,
  repeat = false,
  sound = false,
}) {
  const [output, setOutput] = useState(text);
  const rafRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const run = () => {
      const target = text;
      const len = target.length;
      const queue = Array.from({ length: len }).map((_, i) => ({
        from: GLYPHS[Math.floor(Math.random() * GLYPHS.length)],
        to: target[i],
        start: Math.floor((i * stagger) / 16),
        end:
          Math.floor((i * stagger) / 16) +
          cycles +
          Math.floor(Math.random() * 10),
      }));

      let frame = 0;
      let rafCount = 0;
      let prevComplete = 0;
      const tick = () => {
        if (cancelled) return;
        // Throttle: only progress every `frameStep` rAF ticks (slows animation)
        rafCount++;
        if (rafCount % frameStep !== 0) {
          rafRef.current = requestAnimationFrame(tick);
          return;
        }
        let out = "";
        let complete = 0;
        for (let i = 0; i < queue.length; i++) {
          const { from, to, start, end } = queue[i];
          if (frame >= end) {
            complete++;
            out += to;
          } else if (frame >= start) {
            if (Math.random() < 0.18) {
              queue[i].from = GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
            }
            out += `<span class="chaos-glitch">${queue[i].from}</span>`;
          } else {
            out += to === " " ? " " : `<span class="chaos-hidden">${to}</span>`;
          }
        }
        setOutput(out);
        // Fire a tick when a new character resolves (rate-limited inside playTypeTick)
        if (sound && complete > prevComplete) {
          playTypeTick();
          prevComplete = complete;
        }
        if (complete < queue.length) {
          frame++;
          rafRef.current = requestAnimationFrame(tick);
        } else if (repeat) {
          timeoutRef.current = setTimeout(run, 2400);
        }
      };
      tick();
    };

    run();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <Tag
      className={className}
      data-testid="text-chaos"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: output }}
    />
  );
}
